import { makeAutoObservable } from 'mobx'

import client from 'api/client'
import { backoffFullJitter } from 'utils/retry'

import { type RootStore } from './RootStore'
import { createUseStore } from './util'
import { Auth } from 'types'

const JWT_REFRESH = 240000
const JWT_REFRESH_RETRY_CAP = 10000

export class AuthStore {
  root: RootStore
  jwt?: string = ''
  id?: number

  private refreshAbort?: AbortController

  constructor(root: RootStore, auth?: Auth | null) {
    this.root = root
    this.root.docStore?.setAuth(this)
    this.id = auth?.id

    makeAutoObservable(this)

    if (!!auth) this.startRefresh()
  }

  setToken(jwt: string) {
    this.jwt = jwt
  }

  /** Start refreshing credentials. */
  startRefresh() {
    if (this.refreshAbort) return

    this.refreshAbort = new AbortController()
    // eslint-disable-next-line no-console
    this.refreshToken(this.refreshAbort.signal).catch((e) => console.error(e))
  }

  /** Stop refreshing credentials. */
  stopRefresh() {
    if (!this.refreshAbort) return

    this.refreshAbort.abort()
    this.refreshAbort = undefined
  }

  destroy() {
    this.stopRefresh()
  }

  /**
   * Fetches a fresh JWT every JWT_REFRESH
   * If there is an error fall back on an exponential backoff retry strategy
   */
  private refreshToken = async (abort: AbortSignal) => {
    let attempt = 0
    let wait = 0

    while (!abort.aborted) {
      try {
        const resp = await client.getAuthInfo()
        if (resp.type === 'success' && resp.status === 200) {
          attempt = 0
          const token = resp.headers['x-asymmetric-jwt']
          this.setToken(token)
          wait = Math.random() * JWT_REFRESH
        } else {
          throw Error('Could not retrieve token')
        }
      } catch {
        attempt++
        wait = backoffFullJitter(50, JWT_REFRESH_RETRY_CAP, attempt)
      }
      await new Promise((res) => setTimeout(res, wait))
    }
  }
}

export const [AuthStoreContext, useAuthStore] = createUseStore(AuthStore)
